<template>
<div class="page">
    <ExaminerNavBar />
    <div class="container container--narrow">
        <div class="page-head rise">
            <h1>Create an Assessment</h1>
            <p>Schedule a timed exam, pick the roster and the questions.</p>
        </div>

        <form class="glass card rise rise-1" @submit.prevent="addExamForm" @reset="onReset">
            <div class="field">
                <label for="exam-name">Exam name</label>
                <input id="exam-name" type="text" class="input" placeholder="e.g. Backend Hiring Round 1"
                    :class="{'is-invalid': errors.name===false, 'is-valid': errors.name===true}"
                    v-model="exam.name" @input="validateName()">
                <p class="err" v-if="errors.name===false">Name is required</p>
            </div>

            <div class="form-grid">
                <div class="field">
                    <label for="exam-date">Date</label>
                    <input id="exam-date" type="date" class="input"
                        :class="{'is-invalid': errors.date===false, 'is-valid': errors.date===true}"
                        v-model="exam.date" @input="validateDate()">
                    <p class="err" v-if="errors.date===false">Date is required</p>
                </div>
                <div class="field">
                    <label for="exam-start">Start time</label>
                    <input id="exam-start" type="time" class="input"
                        :class="{'is-invalid': errors.startTime===false, 'is-valid': errors.startTime===true}"
                        v-model="exam.startTime" @input="validateStartTime()">
                    <p class="err" v-if="errors.startTime===false">Start time is required</p>
                </div>
                <div class="field">
                    <label for="exam-end">End time</label>
                    <input id="exam-end" type="time" class="input"
                        :class="{'is-invalid': errors.endTime.required===false || errors.endTime.lessStartTime===false,
                                 'is-valid': errors.endTime.required===true && errors.endTime.lessStartTime===true}"
                        v-model="exam.endTime" @input="validateEndTime()">
                    <p class="err" v-if="errors.endTime.required === false">End time is required</p>
                    <p class="err" v-if="errors.endTime.lessStartTime === false">End time must be after start time</p>
                </div>
            </div>

            <div class="field">
                <label for="exam-examinees">Examinees</label>
                <select id="exam-examinees" class="select"
                    :class="{'is-invalid': errors.examinees===false, 'is-valid': errors.examinees===true}"
                    @change="onExamineeSelect">
                    <option value="">＋ Add an examinee…</option>
                    <option v-for="examinee in examinees" :key="examinee._id" :value="examinee._id"
                        :disabled="isExamineeAlreadyAdded(examinee._id)">{{examinee.name}}</option>
                </select>
                <p class="err" v-if="errors.examinees===false">At least one examinee is required</p>
                <div class="flex gap-2 flex-wrap mt-2" v-if="exam.examinees.length">
                    <button type="button" class="chip chip--btn" v-for="(id, i) in exam.examinees" :key="id"
                        @click="removeExaminee(i)" title="Remove">👤 {{nameOf(id)}} ✕</button>
                </div>
            </div>

            <div class="field">
                <label for="exam-questions">Questions</label>
                <select id="exam-questions" class="select"
                    :class="{'is-invalid': errors.questions===false, 'is-valid': errors.questions===true}"
                    @change="onQuestionSelect">
                    <option value="">＋ Add a question…</option>
                    <option v-for="question in questions" :key="question._id" :value="question._id"
                        :disabled="isQuestionAlreadyAdded(question._id)">{{question.name}}</option>
                </select>
                <p class="err" v-if="errors.questions===false">At least one question is required</p>
                <div class="flex gap-2 flex-wrap mt-2" v-if="exam.questions.length">
                    <button type="button" class="chip chip--alt chip--btn" v-for="(id, i) in exam.questions" :key="id"
                        @click="removeQuestion(i)" title="Remove">⌘ {{questionNameOf(id)}} ✕</button>
                </div>
            </div>

            <div class="flex gap-2 mt-4">
                <button type="submit" class="btn btn--primary" :disabled="addStatus=='LOADING'">
                    <span class="spinner spinner--dark" v-if="addStatus=='LOADING'"></span>
                    <span v-else>Create Exam</span>
                </button>
                <button type="reset" class="btn btn--ghost">Reset</button>
            </div>
        </form>
    </div>
</div>
</template>
<script>
import {addExam} from '../services/assesments';
import {required} from 'vuelidate/lib/validators';
import ExaminerNavBar from './ExaminerNavBar.vue';
import {getAllExaminees} from '../services/users';
import {getAllQuestions} from '../services/questions';
import Vue from 'vue';
export default {
    name: 'AddExamPage',
    data () {
        return {
            status: '',
            addStatus: '',
            exam: {
                name: '',
                date: '',
                startTime: '',
                endTime: '',
                examinees: [],
                questions: [],
            },
            errors: {
                endTime: {

                }
            },
            examinees: [],
            questions: [],
        }
    },
    methods: {
        nameOf(id) {
            const found = this.examinees.find(e => e._id == id);
            return found ? found.name : id;
        },
        questionNameOf(id) {
            const found = this.questions.find(q => q._id == id);
            return found ? found.name : id;
        },
        onExamineeSelect(e) {
            if (e.target.value) {
                this.exam.examinees.push(e.target.value);
                e.target.value = '';
                this.validateExaminees();
            }
        },
        onQuestionSelect(e) {
            if (e.target.value) {
                this.exam.questions.push(e.target.value);
                e.target.value = '';
                this.validateQuestions();
            }
        },
        removeExaminee(i) {
            this.exam.examinees.splice(i, 1);
            this.validateExaminees();
        },
        removeQuestion(i) {
            this.exam.questions.splice(i, 1);
            this.validateQuestions();
        },
        async getAllExaminees() {
            this.status='LOADING';
            try {
                const response = await getAllExaminees();
                this.examinees = response.data;
                this.status = "LOADED";
            }
            catch (error) {
                this.status = "ERROR";
                this.error = error.message;
            }
        },
        async getAllQuestions() {
            this.status='LOADING';
            try {
                const response = await getAllQuestions();
                this.questions = response.data;
                this.status = "LOADED";
            }
            catch (error) {
                this.status = "ERROR";
                this.error = error.message;
            }
        },
        async addExamForm(){
            this.$v.$touch();
            if(this.validateExam()) {
                const dateArray = this.exam.date.split("-");
                const startTimeArray = this.exam.startTime.split(":");
                const endTimeArray = this.exam.endTime.split(":")

                const data = {
                    name: this.exam.name,
                    date: {
                        date: parseInt(dateArray[2]),
                        month: parseInt(dateArray[1]),
                        year: parseInt(dateArray[0])
                    },
                    startTime: {
                        hours: parseInt(startTimeArray[0]),
                        minutes: parseInt(startTimeArray[1])
                    },
                    endTime: {
                        hours: parseInt(endTimeArray[0]),
                        minutes: parseInt(endTimeArray[1])
                    },
                    examinees: this.exam.examinees,
                    questions: this.exam.questions
                }
                try{
                    this.addStatus = 'LOADING'
                    const response = await addExam(data);
                    Vue.$toast.success('Exam - '+response.data.name +' Added Successfully');
                    this.exam = { name: '', date: '', startTime: '', endTime: '', examinees: [], questions: [] };
                    this.onReset();

                } catch(error){
                    Vue.$toast.error(error.message);
                }
                this.addStatus = 'LOADED';
            }
            else {
                Vue.$toast.error('Please Enter Valid Exam Credentials');
                this.status = 'INVALID'
            }
        },
        validateExam() {

            this.validateName();
            this.validateDate();
            this.validateStartTime();
            this.validateEndTime();
            this.validateExaminees();
            this.validateQuestions();
            if (this.errors.name &&
                    this.errors.date &&
                        this.errors.startTime &&
                            this.errors.endTime.required && this.errors.endTime.lessStartTime &&
                                this.errors.examinees &&
                                    this.errors.questions)
            return true;
            return false;
        },
        validateName() {
            let nameError = null;
            if (this.exam.name == '')
                nameError = false;
            else
                nameError = true;
            this.errors = {
                ...this.errors,
                name : nameError
            }
        },
        validateDate() {
            let dateError = null;
            if (this.exam.date == '')
                dateError = false;
            else
                dateError = true;
            this.errors = {
                ...this.errors,
                date : dateError
            }
        },
        validateStartTime() {
            let startTime = null;
            if (this.exam.startTime == '')
                startTime = false;
            else
                startTime = true;
            this.errors = {
                ...this.errors,
                startTime
            }
            let lessStartTime = null;
            if (this.validEndTime()) {
                lessStartTime = true;
            }
            else
            lessStartTime = false;
            this.errors = {
                ...this.errors,
                endTime : {
                    ...this.errors.endTime,
                    lessStartTime
                }
            }
        },
         validateEndTime() {
            let required = null;
            if (this.exam.endTime == '')
                required = false;
            else
                required = true;
            this.errors = {
                ...this.errors,
                endTime : {
                    ...this.errors.endTime,
                    required
                }
            }
            let lessStartTime = null;
            if (this.validEndTime()) {
                lessStartTime = true;
            }
            else
            lessStartTime = false;
            this.errors = {
                ...this.errors,
                endTime : {
                    ...this.errors.endTime,
                    lessStartTime
                }
            }
        },
        validEndTime() {
            if (this.exam.startTime == '')
            return false;
            const startTimeArray = this.exam.startTime.split(":");
            const endTimeArray = this.exam.endTime.split(":")
            let startTime = {
                        hours: parseInt(startTimeArray[0]),
                        minutes: parseInt(startTimeArray[1])
                    };
            let endTime = {
                        hours: parseInt(endTimeArray[0]),
                        minutes: parseInt(endTimeArray[1])
                    };
            if (startTime.hours > endTime.hours)
                return false;
            if (startTime.hours == endTime.hours && startTime.minutes >= endTime.minutes)
                return false;
            return true;
        },
        validateExaminees() {
            let examinees = null;
            if (this.exam.examinees.length == 0)
                examinees = false;
            else
                examinees = true;
            this.errors = {
                ...this.errors,
                examinees
            }
        },
        validateQuestions() {
            let questions = null;
            if (this.exam.questions.length == 0)
                questions = false;
            else
                questions = true;
            this.errors = {
                ...this.errors,
                questions
            }
        },
        setErrorsToNull() {
            this.errors = {
                name: null,
                date: null,
                startTime: null,
                endTime: {
                    required: null,
                    lessStartTime: null },
                examinees: null,
                questions: null,
            }
        },
        onReset() {
            this.setErrorsToNull();
        },
        isQuestionAlreadyAdded(questionId) {
            if (this.exam.questions.indexOf(questionId)==-1)
                return false;
            else
                return true;
        },
        isExamineeAlreadyAdded(examineeId) {
            if (this.exam.examinees.indexOf(examineeId)==-1)
                return false;
            else
                return true;
        }
    },
    validations: {
        exam: {
            name: {
                required
            },
            date: {
                required
            },
            startTime: {
                required
            },
            endTime: {
                required,
            },
            examinees: {
                required: function(value) {
                        return value.length > 0;
                    },
            },
            questions: {
                required: function(value) {
                        return value.length > 0;
                    }
            }
        }
    },
    components: {
        ExaminerNavBar
        },
    created () {
        this.getAllExaminees();
        this.getAllQuestions();
    },
}
</script>
<style>
.form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0 16px;
}
</style>
